import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    X, Save, Loader2, User, Phone, Mail, Calendar, Heart, MapPin, StickyNote, Tag, Users, MessageCircle, Lock,
    Plus, Trash2, AlertTriangle, RotateCcw,
} from 'lucide-react';
import styles from './EditClientDrawer.module.css';

/**
 * Edit a client: name, mobile (with access), email, gender, birthday, anniversary, address, notes,
 * tags, family and WhatsApp offers. Only changed fields are sent, with the time the profile was loaded,
 * so a change someone else saved meanwhile is never overwritten.
 *
 *   <EditClientDrawer customer={c} api={`${SMIIFY_API}/customers`} headers={headers}
 *       canEditPhone canSeePhone focus="family" onClose={...} onSaved={(updated) => ...} />
 */
const TAG_IDEAS = ['VIP', 'Regular', 'Bridal', 'Hair colour', 'Skin care', 'Keratin', 'Kids', 'Senior', 'Corporate', 'Sensitive skin'];
const RELATIONS = ['Wife', 'Husband', 'Mother', 'Father', 'Daughter', 'Son', 'Sister', 'Brother', 'Friend'];
const ymd = (d) => {
    if (!d) return '';
    const x = new Date(d);
    if (Number.isNaN(x.getTime())) return '';
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(x);
};
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
const maskPhone = (p) => { const s = String(p || ''); return s.length >= 6 ? `${s.slice(0, 2)}${'•'.repeat(s.length - 4)}${s.slice(-2)}` : '••••'; };
const GENDERS = ['Female', 'Male', 'Other'];
const LABELS = { name: 'Name', phone: 'Mobile', email: 'Email', gender: 'Gender', dob: 'Birthday', anniversary: 'Anniversary', address: 'Address', notes: 'Notes', tags: 'Tags', family: 'Family', whatsappOffers: 'WhatsApp offers' };

const fromCustomer = (c) => ({
    name: c.name || '',
    phone: c.phone || '',
    email: c.email || '',
    gender: GENDERS.includes(c.gender) ? c.gender : '',
    dob: ymd(c.dob),
    anniversary: ymd(c.anniversary),
    address: c.address || '',
    notes: c.notes || '',
    tags: Array.isArray(c.tags) ? c.tags : [],
    family: (c.family || []).map((m) => ({ _id: m._id, name: m.name || '', relation: m.relation || '', phone: m.phone || '' })),
    whatsappOffers: c.whatsappOffers !== false,
});
const norm = (k, v) => (k === 'family' ? JSON.stringify((v || []).map(({ _id, ...m }) => m)) : JSON.stringify(v));

export default function EditClientDrawer({ customer, api, headers, canEditPhone = false, canSeePhone = true, focus = '', onClose, onSaved }) {
    const start = useMemo(() => fromCustomer(customer), [customer]);
    const [form, setForm] = useState(start);
    const [tagText, setTagText] = useState('');
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [banner, setBanner] = useState(null);      // { tone, text, stale }
    const familyRef = useRef(null);
    const nameRef = useRef(null);

    const changed = useMemo(() => Object.keys(start).filter((k) => norm(k, form[k]) !== norm(k, start[k])), [form, start]);
    const stopByClient = customer.whatsappStopSource === 'REPLY';

    useEffect(() => {
        if (focus === 'family' && familyRef.current) { familyRef.current.scrollIntoView({ block: 'start' }); }
        else if (nameRef.current) nameRef.current.focus();
    }, [focus]);

    const tryClose = () => {
        if (changed.length && !window.confirm('Close without saving your changes?')) return;
        onClose();
    };
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') tryClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => (e[k] ? { ...e, [k]: '' } : e)); };
    const addTag = (raw) => {
        const t = String(raw || '').replace(/,/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 40);
        if (!t) return;
        if (form.tags.some((x) => x.toLowerCase() === t.toLowerCase())) { setTagText(''); return; }
        if (form.tags.length >= 20) { setErrors((e) => ({ ...e, tags: 'At most 20 tags.' })); return; }
        set('tags', [...form.tags, t]);
        setTagText('');
    };
    const setMember = (i, patch) => set('family', form.family.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));

    /* ---------- checks before sending (the server checks the same) ---------- */
    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = 'Name cannot be empty.';
        if (changed.includes('phone') && !/^[6-9]\d{9}$/.test(form.phone)) e.phone = 'Enter a valid 10-digit mobile number.';
        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter a valid email address.';
        if (form.dob && form.dob > today()) e.dob = 'Birthday cannot be in the future.';
        if (form.anniversary && form.anniversary > today()) e.anniversary = 'Anniversary cannot be in the future.';
        const badMember = form.family.find((m) => !m.name.trim() || (m.phone && m.phone.length !== 10));
        if (badMember) e.family = !badMember.name.trim() ? 'Every family member needs a name.' : `Phone for ${badMember.name} must be 10 digits.`;
        setErrors(e);
        return !Object.keys(e).length;
    };

    const save = async () => {
        if (!changed.length || !validate()) return;
        setSaving(true); setBanner(null);
        const body = { expectedUpdatedAt: customer.updatedAt || undefined };
        changed.forEach((k) => {
            if (k === 'family') body.family = form.family.map((m) => ({ ...m, name: m.name.trim(), relation: m.relation.trim() }));
            else if (k === 'dob' || k === 'anniversary') body[k] = form[k] || null;
            else if (typeof form[k] === 'string') body[k] = form[k].trim();
            else body[k] = form[k];
        });
        try {
            const res = await fetch(`${api}/${customer._id}`, { method: 'PUT', headers, body: JSON.stringify(body) });
            const j = await res.json().catch(() => ({}));
            if (!res.ok || j.success === false) {
                if (j.field) setErrors((e) => ({ ...e, [j.field]: j.message }));
                setBanner({ tone: 'err', text: j.message || `Could not save (error ${res.status}).`, stale: j.code === 'STALE' });
                return;
            }
            onSaved(j.data, j.message);
        } catch (err) {
            setBanner({ tone: 'err', text: err.message === 'Failed to fetch' ? 'Cannot reach the server.' : err.message });
        } finally { setSaving(false); }
    };

    const field = (k, label, Icon, input, help) => (
        <div className={`${styles.field} ${changed.includes(k) ? styles.changed : ''}`}>
            <label className={styles.label}><Icon size={12} /> {label}{changed.includes(k) && <span className={styles.dot} aria-label="changed" />}</label>
            {input}
            {errors[k] ? <span className={styles.err}>{errors[k]}</span> : help ? <small className={styles.help}>{help}</small> : null}
        </div>
    );

    return (
        <div className={styles.overlay} role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) tryClose(); }}>
            <aside className={styles.drawer} role="dialog" aria-modal="true" aria-label="Edit client">
                <header className={styles.head}>
                    <div>
                        <h2>Edit client</h2>
                        <p>{customer.name}{canSeePhone ? ` · +91 ${customer.phone}` : ''}</p>
                    </div>
                    <button type="button" className={styles.close} onClick={tryClose} aria-label="Close"><X size={18} /></button>
                </header>

                <div className={styles.body}>
                    {banner && (
                        <div className={`${styles.banner} ${banner.tone === 'err' ? styles.bannerErr : ''}`}>
                            <AlertTriangle size={14} /><span>{banner.text}</span>
                            {banner.stale && <button type="button" onClick={() => window.location.reload()}><RotateCcw size={12} /> Reload</button>}
                        </div>
                    )}

                    {/* ---------------- basic ---------------- */}
                    <section className={styles.section}>
                        <h3>Basic details</h3>
                        <div className={styles.grid}>
                            {field('name', 'Full name', User, <input ref={nameRef} className={`${styles.input} ${errors.name ? styles.inputErr : ''}`} value={form.name} maxLength={120} onChange={(e) => set('name', e.target.value)} />)}
                            {field('phone', 'Mobile', Phone,
                                canEditPhone
                                    ? <div className={styles.prefixed}><span>+91</span><input className={`${styles.input} ${errors.phone ? styles.inputErr : ''}`} value={form.phone} inputMode="numeric" maxLength={10} onChange={(e) => set('phone', e.target.value.replace(/\D/g, '').slice(0, 10))} /></div>
                                    : <div className={`${styles.input} ${styles.locked}`}><Lock size={12} /> +91 {canSeePhone ? customer.phone : maskPhone(customer.phone)}</div>,
                                canEditPhone ? (changed.includes('phone') ? 'Bills, cards and history stay with this client.' : 'Fix a wrong number here.') : 'Ask the owner for "Change client phone" access.')}
                            {field('email', 'Email', Mail, <input type="email" className={`${styles.input} ${errors.email ? styles.inputErr : ''}`} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="name@example.com" />)}
                            {field('gender', 'Gender', User, (
                                <div className={styles.segment} role="radiogroup" aria-label="Gender">
                                    {GENDERS.map((g) => (
                                        <button type="button" key={g} role="radio" aria-checked={form.gender === g} className={form.gender === g ? styles.segOn : ''} onClick={() => set('gender', form.gender === g ? '' : g)}>{g}</button>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* ---------------- dates ---------------- */}
                    <section className={styles.section}>
                        <h3>Special days</h3>
                        <div className={styles.grid}>
                            {field('dob', 'Birthday', Calendar, <input type="date" max={today()} className={`${styles.input} ${errors.dob ? styles.inputErr : ''}`} value={form.dob} onChange={(e) => set('dob', e.target.value)} />, 'Birthday offers go out from this.')}
                            {field('anniversary', 'Anniversary', Heart, <input type="date" max={today()} className={`${styles.input} ${errors.anniversary ? styles.inputErr : ''}`} value={form.anniversary} onChange={(e) => set('anniversary', e.target.value)} />)}
                        </div>
                    </section>

                    {/* ---------------- address, notes, tags ---------------- */}
                    <section className={styles.section}>
                        <h3>More about the client</h3>
                        {field('address', 'Address', MapPin, <input className={styles.input} value={form.address} maxLength={300} onChange={(e) => set('address', e.target.value)} placeholder="House, street, area" />)}
                        {field('notes', 'Notes', StickyNote, (
                            <>
                                <textarea className={styles.input} rows={3} maxLength={1000} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Allergies, preferred products, favourite stylist..." />
                                <span className={styles.counter}>{form.notes.length}/1000</span>
                            </>
                        ))}
                        {field('tags', 'Tags', Tag, (
                            <>
                                <div className={styles.tagBox}>
                                    {form.tags.map((t) => (
                                        <span key={t} className={styles.tag}>{t}<button type="button" onClick={() => set('tags', form.tags.filter((x) => x !== t))} aria-label={`Remove ${t}`}><X size={11} /></button></span>
                                    ))}
                                    <input
                                        value={tagText}
                                        onChange={(e) => setTagText(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagText); }
                                            if (e.key === 'Backspace' && !tagText && form.tags.length) set('tags', form.tags.slice(0, -1));
                                        }}
                                        onBlur={() => addTag(tagText)}
                                        placeholder={form.tags.length ? 'Add tag' : 'Type and press Enter'}
                                        aria-label="Add a tag"
                                    />
                                </div>
                                <div className={styles.ideas}>
                                    {TAG_IDEAS.filter((t) => !form.tags.some((x) => x.toLowerCase() === t.toLowerCase())).slice(0, 6).map((t) => (
                                        <button type="button" key={t} onClick={() => addTag(t)}><Plus size={10} /> {t}</button>
                                    ))}
                                </div>
                            </>
                        ), 'Find clients by tag, and pick them for campaigns.')}
                    </section>

                    {/* ---------------- WhatsApp ---------------- */}
                    <section className={styles.section}>
                        <h3>Messages</h3>
                        <div className={`${styles.switchRow} ${changed.includes('whatsappOffers') ? styles.changed : ''}`}>
                            <div>
                                <strong><MessageCircle size={13} /> WhatsApp offers</strong>
                                <span>
                                    {stopByClient
                                        ? 'The client replied STOP. Offers start again only when the client messages the salon.'
                                        : 'Offers and campaigns on WhatsApp. Bills and booking messages are always sent.'}
                                </span>
                            </div>
                            <label className={styles.switch}>
                                <input type="checkbox" checked={form.whatsappOffers} disabled={stopByClient} onChange={(e) => set('whatsappOffers', e.target.checked)} aria-label="WhatsApp offers" />
                                <span />
                            </label>
                        </div>
                        {errors.whatsappOffers && <span className={styles.err}>{errors.whatsappOffers}</span>}
                    </section>

                    {/* ---------------- family ---------------- */}
                    <section className={styles.section} ref={familyRef}>
                        <div className={styles.secRow}>
                            <h3>Family <small>({form.family.length})</small>{changed.includes('family') && <span className={styles.dot} aria-label="changed" />}</h3>
                            <button type="button" className={styles.smallBtn} onClick={() => set('family', [...form.family, { name: '', relation: '', phone: '' }])} disabled={form.family.length >= 20}>
                                <Plus size={12} /> Add member
                            </button>
                        </div>
                        {form.family.length === 0 && <p className={styles.empty}>No family members yet. Add them so their visits link to this client.</p>}
                        <datalist id="client-relations">{RELATIONS.map((r) => <option key={r} value={r} />)}</datalist>
                        {form.family.map((m, i) => (
                            <div key={m._id || `new-${i}`} className={styles.member}>
                                <input className={styles.input} value={m.name} placeholder="Name" maxLength={80} onChange={(e) => setMember(i, { name: e.target.value })} aria-label={`Family member ${i + 1} name`} />
                                <input className={styles.input} value={m.relation} placeholder="Relation" list="client-relations" maxLength={40} onChange={(e) => setMember(i, { relation: e.target.value })} aria-label={`Family member ${i + 1} relation`} />
                                <input className={styles.input} value={m.phone} placeholder="Mobile (optional)" inputMode="numeric" maxLength={10} onChange={(e) => setMember(i, { phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} aria-label={`Family member ${i + 1} mobile`} />
                                <button type="button" className={styles.iconBtn} onClick={() => set('family', form.family.filter((_, idx) => idx !== i))} aria-label={`Remove ${m.name || 'member'}`}><Trash2 size={13} /></button>
                            </div>
                        ))}
                        {errors.family && <span className={styles.err}>{errors.family}</span>}
                    </section>
                </div>

                <footer className={styles.foot}>
                    <span className={styles.summary}>
                        {changed.length ? <><b>{changed.length}</b> change{changed.length === 1 ? '' : 's'}: {changed.map((k) => LABELS[k]).join(', ')}</> : 'No changes yet'}
                    </span>
                    <div className={styles.footBtns}>
                        {changed.length > 0 && <button type="button" className={styles.ghost} onClick={() => { setForm(start); setErrors({}); setBanner(null); }} disabled={saving}>Undo all</button>}
                        <button type="button" className={styles.ghost} onClick={tryClose} disabled={saving}>Cancel</button>
                        <button type="button" className={styles.primary} onClick={save} disabled={saving || !changed.length}>
                            {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />} Save changes
                        </button>
                    </div>
                </footer>
            </aside>
        </div>
    );
}
